import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  DESCRIPTION, MA_ID, NAME, QUESTIONS, STANDARD_ID, STATUS,
} from 'constants/formFields';
import { selectMaterialId } from 'store/slices/materialAssessment/selectors';
import { useGetSingleSurveyQuery } from 'store/slices/survey/apis/surveyApi';
import getSurveyDefaultValues from 'utils/formHelpers/getSurveyDefaultValues';

import { SURVEY_STATUS } from '../../../../../../constants/materialAssessment';
import { createSurveySchema } from '../../../../../../utils/validators/MASurveyValidators';

import type { TCreateSurveySchema } from '../../../../../../utils/validators/MASurveyValidators';

const useCreateSurveyForm = ({
  surveyId, showStandards, recordId,
}: { surveyId?: string, showStandards?: boolean, recordId?: string }) => {
  const fetchQuery = surveyId ? { id: surveyId } : skipToken;

  const { data: singleSurvey } = useGetSingleSurveyQuery(fetchQuery);

  const maIdFromStore = useSelector(selectMaterialId);
  let maId: number | undefined;
  if (recordId) {
    maId = Number(recordId);
  } else if (maIdFromStore && maIdFromStore !== -1) {
    maId = maIdFromStore;
  } else {
    maId = undefined;
  }

  const {
    control,
    handleSubmit,
    formState: { isDirty, isLoading, errors },
    reset,
    setError,
    getValues,
    watch,
    setValue,
  } = useForm<TCreateSurveySchema>({
    mode: 'onChange',
    resolver: yupResolver(createSurveySchema(showStandards)),
    defaultValues: getSurveyDefaultValues({
      values: singleSurvey?.data,
      maId: maId && maId !== -1 ? maId : undefined,
    }),
  });

  useEffect(() => {
    if (!surveyId || surveyId === 'new') {
      reset({
        [NAME]: '',
        [DESCRIPTION]: '',
        [MA_ID]: maId || 0,
        [STANDARD_ID]: undefined,
        [STATUS]: SURVEY_STATUS.DRAFT,
        [QUESTIONS]: [],
      });
    } else if (singleSurvey?.data) {
      reset(getSurveyDefaultValues({
        values: singleSurvey?.data,
        maId,
      }));
    }
  }, [surveyId, reset, singleSurvey, maId]);

  const fieldsName = Object.keys(getValues());

  return {
    control,
    handleSubmit,
    isDirty,
    isLoading,
    errors,
    setError,
    getValues,
    watch,
    setValue,
    reset,
    fieldsName,
  };
};

export default useCreateSurveyForm;
